// apps/web/src/components/ErrorBoundary/ErrorBoundary.tsx
// ─────────────────────────────────────────────────────────────────────────
// Render-error boundary. A throw during render/commit anywhere below this
// component is caught here instead of unmounting the whole React tree to a
// blank page. We use a class component because that is the only React API that
// catches render errors — hooks cannot.
//
// PLACEMENT (see App.tsx): one instance wraps the routed content INSIDE the
// layout, so a crash in a single page keeps the sidebar/header and offers a
// local recovery; a second instance wraps the entire app as a last resort for
// a crash in the chrome itself.
//
// The fallback is deliberately quiet: it states what happened, gives two
// concrete actions (try again / go home), and shows a support-quotable trace
// id when the underlying error carried one (server ProblemDetails). It does
// NOT apologize or expose a stack to the user.
// ─────────────────────────────────────────────────────────────────────────

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { extractTraceId } from '@nisk/core';
import styles from './ErrorBoundary.module.scss';

interface ErrorBoundaryProps {
	children: ReactNode;
	/**
	 * Label for the boundary, included in logs so a report tells you WHERE it
	 * broke ("route" vs "app-shell"). Not shown to users.
	 */
	scope?: string;
	/**
	 * Optional custom fallback. When provided it fully replaces the default UI;
	 * it receives the error and a reset callback so a caller can render its own
	 * recovery affordance. Most callers should use the default.
	 */
	fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
	error: Error | null;
	/** Server correlation id, if the thrown error's message embedded one. */
	traceId: string | null;
}

export default class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { error: null, traceId: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		// A SyncHttpError's message contains the body; try to recover a traceId
		// so the fallback can show it even for a network error that bubbled up
		// through a render path.
		return { error, traceId: extractTraceId(error.message) };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// Single structured log line. In production this is where you'd also POST
		// to a telemetry endpoint; kept console-only for now to avoid a failing
		// report compounding the original error.
		const scope = this.props.scope ?? 'unknown';
		console.error(
			`[error-boundary:${scope}] render error`,
			error,
			info.componentStack,
		);
	}

	private reset = (): void => {
		this.setState({ error: null, traceId: null });
	};

	private reload = (): void => {
		// Hard reload — clears any wedged in-memory state the retry can't.
		window.location.reload();
	};

	private goHome = (): void => {
		// Full navigation (not client-side) so we also drop the crashed subtree.
		window.location.assign('/');
	};

	render(): ReactNode {
		const { error, traceId } = this.state;
		if (!error) return this.props.children;

		if (this.props.fallback) return this.props.fallback(error, this.reset);

		return (
			<div
				className={styles.wrap}
				role='alert'
				aria-live='assertive'>
				<div className={styles.card}>
					<span
						className={styles.mark}
						aria-hidden='true'
					/>
					<h1 className={styles.title}>This page hit a snag</h1>
					<p className={styles.body}>
						The page stopped loading before it finished. Your
						progress is saved. Try again, or head back home.
					</p>

					<div className={styles.actions}>
						<button
							type='button'
							className={styles.primary}
							onClick={this.reload}>
							Try again
						</button>
						<button
							type='button'
							className={styles.secondary}
							onClick={this.goHome}>
							Go home
						</button>
					</div>

					{traceId && (
						<p className={styles.trace}>
							Reference:{' '}
							<code className={styles.code}>{traceId}</code>
						</p>
					)}
				</div>
			</div>
		);
	}
}
